import React, { useEffect, useState } from "react";
import { Alert, Button, Col, Row } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../api/client";
import EmptyState from "../components/common/EmptyState";
import LoadingState from "../components/common/LoadingState";
import PageHeader from "../components/common/PageHeader";
import { downloadApiFile } from "../utils/download";

function LookupManagerPage({ title, endpoint, fields, listTitle, createPath, editPathBase }) {
  const history = useHistory();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchItems();
  }, []);

  async function fetchItems() {
    setLoading(true);
    setError("");

    try {
      const response = await api.get(endpoint);
      setItems(response.data || []);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load records.");
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(uuid) {
    try {
      await api.delete(`${endpoint}/${uuid}`);
      toast.success(`${title} deleted`);
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to delete ${title.toLowerCase()}.`);
    }
  }

  async function handleDownload() {
    setDownloading(true);

    try {
      await downloadApiFile(`${endpoint}/export`, {
        fallbackFileName: `${title.toLowerCase()}.csv`
      });
    } catch (err) {
      toast.error(err.response?.data?.error || `Failed to download ${title.toLowerCase()}.`);
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div>
      <PageHeader
        title={title}
        action={
          <div className="d-flex gap-2">
            <Button variant="primary" onClick={() => history.push(createPath)}>
              Add {title}
            </Button>
            <Button variant="outline-primary" onClick={handleDownload} disabled={downloading || loading} title={`Download ${title.toLowerCase()}`}>
              {downloading ? "Downloading..." : "Download"}
            </Button>
          </div>
        }
      />

      <Row className="g-4">
        <Col xs={12}>
          <div className="content-card table-card">
            <h5 className="mb-3">{listTitle || `${title} list`}</h5>
            {loading ? <LoadingState label={`Loading ${title.toLowerCase()}...`} /> : null}
            {error ? <Alert variant="danger">{error}</Alert> : null}
            {!loading && !error && !items.length ? (
              <EmptyState title={`No ${title.toLowerCase()} yet`} text={`Create the first ${title.toLowerCase()} from the add page.`} />
            ) : null}

            {!loading && items.length ? (
              <div className="table-responsive">
                <table className="table align-middle mb-0">
                  <thead>
                    <tr>
                      {fields.map((field) => (
                        <th key={field.name}>{field.label}</th>
                      ))}
                      <th className="text-end">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.uuid}>
                        {fields.map((field) => (
                          <td key={field.name}>{item[field.name] || "-"}</td>
                        ))}
                        <td className="text-end">
                          <div className="d-inline-flex gap-2">
                            <Button variant="outline-secondary" size="sm" onClick={() => history.push(`${editPathBase}/${item.uuid}/edit`)}>
                              Edit
                            </Button>
                            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(item.uuid)}>
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default LookupManagerPage;
